import { useQuery } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './lib/api';
import { usePlanVariants } from './lib/usePlanVariants';
import Login from './pages/Login';
import Roster from './pages/Roster';
import RosterSelfReports from './pages/RosterSelfReports';
import PlayerAccess from './pages/PlayerAccess';
import AdminChatBans from './pages/AdminChatBans';
import Users from './pages/Users';
import Events from './pages/Events';
import EventStats from './pages/EventStats';
import EventsVs from './pages/EventsVs';
import EventsVsDetail from './pages/EventsVsDetail';
import EventsVsOverview from './pages/EventsVsOverview';
import EventDetail from './pages/EventDetail';
import WeekDashboard from './pages/WeekDashboard';
import CSPlan from './pages/CSPlan';
import DSPlan from './pages/DSPlan';
import Surveys from './pages/Surveys';
import SurveyDetail from './pages/SurveyDetail';
import StrengthRequests from './pages/StrengthRequests';
import StrengthRequestDetail from './pages/StrengthRequestDetail';
import StrengthStats from './pages/StrengthStats';
import PhotoAlbums from './pages/PhotoAlbums';
import PhotoAlbumDetail from './pages/PhotoAlbumDetail';
import PublicAlbum from './pages/PublicAlbum';
import PublicBoard from './pages/PublicBoard';
import SelfReport from './pages/SelfReport';
import PlayerHubMe from './pages/PlayerHubMe';
import PlayerHubStats from './pages/PlayerHubStats';
import PlayerHubAlliance from './pages/PlayerHubAlliance';
import PlayerHubHallOfFame from './pages/PlayerHubHallOfFame';
import PlayerHubSwap from './pages/PlayerHubSwap';
import Trains from './pages/Trains';
import Account from './pages/Account';
import AdminScorePolicy from './pages/AdminScorePolicy';
import AdminPhrases from './pages/AdminPhrases';
import AdminWorstParker from './pages/AdminWorstParker';
import AdminHallOfFame from './pages/AdminHallOfFame';
import AdminKillGrowth from './pages/AdminKillGrowth';
import AdminInsights from './pages/AdminInsights';
import AdminAlliances from './pages/AdminAlliances';
import AdminTransferWindows from './pages/AdminTransferWindows';
import AdminTransferWindowDetail from './pages/AdminTransferWindowDetail';
import AdminTransferWindowKick from './pages/AdminTransferWindowKick';
import WorldClock from './pages/WorldClock';
import AllianceSettings from './pages/AllianceSettings';
import Superadmin from './pages/Superadmin';
import Layout from './components/Layout';

export default function App() {
  const location = useLocation();
  const isPublic =
    location.pathname.startsWith('/public/') ||
    location.pathname.startsWith('/a/') ||
    location.pathname.startsWith('/self-report/') ||
    location.pathname.startsWith('/players/');

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me().catch(() => null),
    enabled: !isPublic,
  });
  const playerSessionQuery = useQuery({
    queryKey: ['player-session'],
    queryFn: () => api.playerSession().catch(() => null),
    enabled: !isPublic && meQuery.isFetched && meQuery.data == null,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  // Hydrate the custom plan-variant registry once the user is logged in.
  usePlanVariants();

  if (isPublic) {
    return (
      <Routes>
        <Route path="/public/albums/:slug" element={<PublicAlbum />} />
        <Route path="/a/:slug" element={<PublicAlbum />} />
        <Route path="/public/board" element={<PublicBoard />} />
        <Route path="/public/:slug/board" element={<PublicBoard />} />
        <Route
          path="/self-report/:token"
          element={<LegacySelfReportRedirect />}
        />
        <Route path="/players/:token/self-report" element={<SelfReport />} />
        <Route path="/players/:token/stats" element={<PlayerHubStats />} />
        <Route path="/players/:token/me" element={<PlayerHubMe />} />
        <Route
          path="/players/:token/alliance"
          element={<PlayerHubAlliance />}
        />
        <Route
          path="/players/:token/hall-of-fame"
          element={<PlayerHubHallOfFame />}
        />
        <Route
          path="/players/:token/events/:eventId/swap"
          element={<PlayerHubSwap />}
        />
        <Route
          path="/players/:token"
          element={<PlayerRootRedirect />}
        />
        <Route path="/public/*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  if (meQuery.isLoading) {
    return <div className="flex h-full items-center justify-center text-slate-400">Loading…</div>;
  }

  const user = meQuery.data ?? null;

  if (!user) {
    // If a magic-link session is present, drop them on their hub — unless
    // they explicitly navigated to /login (admin sign-in).
    if (playerSessionQuery.isLoading) {
      return <div className="flex h-full items-center justify-center text-slate-400">Loading…</div>;
    }
    const playerToken = playerSessionQuery.data?.token ?? null;
    if (playerToken && location.pathname !== '/login') {
      return (
        <Navigate
          to={`/players/${encodeURIComponent(playerToken)}/self-report`}
          replace
        />
      );
    }
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const canConsole = user.isSuperadmin || user.isServerAdmin;
  const hasTransfers = user.alliance?.features.includes('transfers') ?? false;

  // Accounts without an (active) alliance — e.g. a pure superadmin — only
  // get the console.
  if (!user.alliance) {
    return (
      <Layout user={user}>
        <Routes>
          {canConsole && <Route path="/superadmin" element={<Superadmin user={user} />} />}
          {user.isSuperadmin && (
            <Route path="/admin/phrases" element={<AdminPhrases user={user} />} />
          )}
          <Route path="/account" element={<Account user={user} />} />
          <Route
            path="*"
            element={<Navigate to={canConsole ? '/superadmin' : '/account'} replace />}
          />
        </Routes>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/roster" element={<Roster user={user} />} />
        <Route path="/roster/self-reports" element={<RosterSelfReports user={user} />} />
        <Route path="/roster/access" element={<PlayerAccess user={user} />} />
        <Route path="/roster/chat-bans" element={<AdminChatBans user={user} />} />
        <Route path="/roster/kill-growth" element={<AdminKillGrowth user={user} />} />
        <Route path="/roster/insights" element={<AdminInsights user={user} />} />
        <Route path="/dashboard" element={<WeekDashboard />} />
        <Route path="/events" element={<Events user={user} />} />
        <Route path="/events/stats" element={<EventStats user={user} />} />
        <Route path="/events/vs" element={<EventsVs user={user} />} />
        <Route path="/events/vs/overview" element={<EventsVsOverview user={user} />} />
        <Route path="/events/vs/:id" element={<EventsVsDetail user={user} />} />
        <Route path="/events/:id" element={<EventDetail user={user} />} />
        <Route path="/plans/cs" element={<CSPlan />} />
        <Route path="/plans/ds" element={<DSPlan />} />
        <Route path="/surveys" element={<Surveys user={user} />} />
        <Route path="/surveys/:id" element={<SurveyDetail user={user} />} />
        <Route path="/strength-requests" element={<StrengthRequests user={user} />} />
        <Route path="/strength-requests/stats" element={<StrengthStats />} />
        <Route path="/strength-requests/:id" element={<StrengthRequestDetail user={user} />} />
        <Route path="/albums" element={<PhotoAlbums user={user} />} />
        <Route path="/albums/:id" element={<PhotoAlbumDetail user={user} />} />
        <Route path="/trains" element={<Trains user={user} />} />
        <Route path="/world-clock" element={<WorldClock user={user} />} />
        <Route path="/users" element={<Users user={user} />} />
        <Route path="/admin/score-policy" element={<AdminScorePolicy user={user} />} />
        <Route path="/admin/phrases" element={<AdminPhrases user={user} />} />
        <Route path="/admin/worst-parker" element={<AdminWorstParker user={user} />} />
        <Route path="/admin/hall-of-fame" element={<AdminHallOfFame user={user} />} />
        <Route path="/admin/alliances" element={<AdminAlliances user={user} />} />
        <Route path="/admin/alliance" element={<AllianceSettings user={user} />} />
        {hasTransfers && (
          <>
            <Route path="/admin/transfers" element={<AdminTransferWindows user={user} />} />
            <Route
              path="/admin/transfers/:id"
              element={<AdminTransferWindowDetail user={user} />}
            />
            <Route
              path="/admin/transfers/:id/kick"
              element={<AdminTransferWindowKick user={user} />}
            />
          </>
        )}
        {canConsole && <Route path="/superadmin" element={<Superadmin user={user} />} />}
        <Route path="/account" element={<Account user={user} />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

function LegacySelfReportRedirect() {
  const path = useLocation().pathname;
  const token = path.replace(/^\/self-report\//, '').replace(/\/$/, '');
  return <Navigate to={`/players/${token}/self-report`} replace />;
}

function PlayerRootRedirect() {
  const path = useLocation().pathname;
  const token = path.replace(/^\/players\//, '').replace(/\/$/, '');
  return <Navigate to={`/players/${token}/self-report`} replace />;
}
